import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "@/hooks/use-toast";
import { db } from "@/lib/firebase";
import { collection, doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { Languages, Plus, Save, Download, Upload, Trash2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const translationKeySchema = z.object({
  category: z.string().min(1, "Category is required").max(50, "Category must be less than 50 characters"),
  key: z.string().min(1, "Key is required").max(100, "Key must be less than 100 characters"),
  frValue: z.string().min(1, "French translation is required").max(1000, "Translation must be less than 1000 characters"),
  enValue: z.string().min(1, "English translation is required").max(1000, "Translation must be less than 1000 characters"),
});

type TranslationKeyForm = z.infer<typeof translationKeySchema>;

interface Translation {
  [key: string]: any;
}

const I18nManager = () => {
  const { user } = useAuth();
  const [frTranslations, setFrTranslations] = useState<Translation>({});
  const [enTranslations, setEnTranslations] = useState<Translation>({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>("nav");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm<TranslationKeyForm>({
    resolver: zodResolver(translationKeySchema),
    defaultValues: {
      category: "nav",
    },
  });

  useEffect(() => {
    loadTranslations();
  }, []);

  const loadTranslations = async () => {
    try {
      const frDoc = await getDoc(doc(db, "i18n", "fr"));
      const enDoc = await getDoc(doc(db, "i18n", "en"));

      if (frDoc.exists()) {
        setFrTranslations(frDoc.data().translations || {});
      } else {
        // Initialize with default translations
        const defaultFr = {
          nav: {
            services: "Services",
            sellers: "Vendeurs",
            buyers: "Acheteurs",
            consultants: "Consultants",
            brokers: "Courtiers",
            ecosystem: "Écosystème",
            internal_partners: "Partenaires internes",
            lenders: "Prêteurs",
            business_partners: "Partenaires d'affaires internes",
            opportunities_hub: "Hub d'opportunités",
            resources: "Ressources",
            deal_monitor: "Deal Monitor",
            blog: "Blog",
            elearning: "E-learning",
            faq: "FAQ",
            about: "À propos",
            contact: "Contact",
          },
          index: {
            hero: {
              subtitle: "La plateforme qui relie vendeurs, acheteurs, consultants et partenaires.",
            },
          },
          cta: {
            join_ecosystem_title: "Faites partie de l'écosystème d'AcquizGroup",
            join_ecosystem_desc: "Rejoignez notre réseau de prêteurs et partenaires d'affaires vérifiés.",
            join_ecosystem_short: "Rejoindre l'écosystème",
            i_am_lender: "Je suis Prêteur",
            i_am_internal_partner: "Je suis Partenaire interne",
          },
        };
        setFrTranslations(defaultFr);
        await setDoc(doc(db, "i18n", "fr"), {
          translations: defaultFr,
          updatedAt: serverTimestamp(),
        });
      }

      if (enDoc.exists()) {
        setEnTranslations(enDoc.data().translations || {});
      } else {
        // Initialize with default translations
        const defaultEn = {
          nav: {
            services: "Services",
            sellers: "Sellers",
            buyers: "Buyers",
            consultants: "Consultants",
            brokers: "Brokers",
            ecosystem: "Ecosystem",
            internal_partners: "Internal Partners",
            lenders: "Lenders",
            business_partners: "Internal Business Partners",
            opportunities_hub: "Opportunities Hub",
            resources: "Resources",
            deal_monitor: "Deal Monitor",
            blog: "Blog",
            elearning: "E-learning",
            faq: "FAQ",
            about: "About",
            contact: "Contact",
          },
          index: {
            hero: {
              subtitle: "The platform connecting sellers, buyers, consultants and partners.",
            },
          },
          cta: {
            join_ecosystem_title: "Join AcquizGroup's Ecosystem",
            join_ecosystem_desc: "Join our network of verified lenders and business partners.",
            join_ecosystem_short: "Join Ecosystem",
            i_am_lender: "I'm a Lender",
            i_am_internal_partner: "I'm an Internal Partner",
          },
        };
        setEnTranslations(defaultEn);
        await setDoc(doc(db, "i18n", "en"), {
          translations: defaultEn,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Error loading translations:", error);
      toast({
        title: "Error",
        description: "Failed to load translations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: TranslationKeyForm) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to manage translations",
        variant: "destructive",
      });
      return;
    }

    try {
      // Update French translations
      const newFrTranslations = { ...frTranslations };
      if (!newFrTranslations[data.category]) {
        newFrTranslations[data.category] = {};
      }
      newFrTranslations[data.category][data.key] = data.frValue;

      // Update English translations
      const newEnTranslations = { ...enTranslations };
      if (!newEnTranslations[data.category]) {
        newEnTranslations[data.category] = {};
      }
      newEnTranslations[data.category][data.key] = data.enValue;

      // Save to Firebase
      await updateDoc(doc(db, "i18n", "fr"), {
        translations: newFrTranslations,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "i18n", "en"), {
        translations: newEnTranslations,
        updatedAt: serverTimestamp(),
      });

      setFrTranslations(newFrTranslations);
      setEnTranslations(newEnTranslations);

      toast({
        title: "Success",
        description: "Translation key added successfully",
      });

      reset({
        category: data.category,
        key: "",
        frValue: "",
        enValue: "",
      });
    } catch (error) {
      console.error("Error saving translation:", error);
      toast({
        title: "Error",
        description: "Failed to save translation",
        variant: "destructive",
      });
    }
  };

  const handleDeleteKey = async (category: string, key: string) => {
    if (!user) return;

    try {
      const newFrTranslations = { ...frTranslations };
      const newEnTranslations = { ...enTranslations };

      if (newFrTranslations[category]) {
        delete newFrTranslations[category][key];
      }
      if (newEnTranslations[category]) {
        delete newEnTranslations[category][key];
      }

      await updateDoc(doc(db, "i18n", "fr"), {
        translations: newFrTranslations,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "i18n", "en"), {
        translations: newEnTranslations,
        updatedAt: serverTimestamp(),
      });

      setFrTranslations(newFrTranslations);
      setEnTranslations(newEnTranslations);

      toast({
        title: "Success",
        description: "Translation key deleted",
      });
    } catch (error) {
      console.error("Error deleting translation:", error);
      toast({
        title: "Error",
        description: "Failed to delete translation",
        variant: "destructive",
      });
    }
  };

  const handleExportJSON = () => {
    const data = {
      fr: frTranslations,
      en: enTranslations,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `i18n-export-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Success",
      description: "Translations exported successfully",
    });
  };

  const handleImportJSON = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);

        if (data.fr && data.en) {
          await updateDoc(doc(db, "i18n", "fr"), {
            translations: data.fr,
            updatedAt: serverTimestamp(),
          });

          await updateDoc(doc(db, "i18n", "en"), {
            translations: data.en,
            updatedAt: serverTimestamp(),
          });

          setFrTranslations(data.fr);
          setEnTranslations(data.en);

          toast({
            title: "Success",
            description: "Translations imported successfully",
          });
        } else {
          throw new Error("Invalid file format");
        }
      } catch (error) {
        console.error("Error importing translations:", error);
        toast({
          title: "Error",
          description: "Failed to import translations. Please check the file format.",
          variant: "destructive",
        });
      }
    };
    reader.readAsText(file);
  };

  const categories = Array.from(
    new Set([...Object.keys(frTranslations), ...Object.keys(enTranslations)])
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Languages className="h-10 w-10 text-primary" />
              <div>
                <h1 className="text-3xl font-bold text-foreground">i18n Translation Manager</h1>
                <p className="text-muted-foreground">Manage multilingual content for your application</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExportJSON}>
                <Download className="h-4 w-4 mr-2" />
                Export JSON
              </Button>
              <label htmlFor="import-json">
                <Button variant="outline" asChild>
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    Import JSON
                  </span>
                </Button>
              </label>
              <input
                id="import-json"
                type="file"
                accept=".json"
                onChange={handleImportJSON}
                className="hidden"
              />
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Add New Translation */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Add New Translation Key
              </CardTitle>
              <CardDescription>Add a new translation key with French and English values</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    placeholder="e.g., nav, cta, index"
                    {...register("category")}
                  />
                  {errors.category && (
                    <p className="text-destructive text-sm mt-1">{errors.category.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="key">Key</Label>
                  <Input id="key" placeholder="e.g., services, about" {...register("key")} />
                  {errors.key && <p className="text-destructive text-sm mt-1">{errors.key.message}</p>}
                </div>

                <div>
                  <Label htmlFor="frValue">French Translation</Label>
                  <Textarea
                    id="frValue"
                    placeholder="Entrez la traduction en français"
                    {...register("frValue")}
                  />
                  {errors.frValue && (
                    <p className="text-destructive text-sm mt-1">{errors.frValue.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="enValue">English Translation</Label>
                  <Textarea
                    id="enValue"
                    placeholder="Enter the English translation"
                    {...register("enValue")}
                  />
                  {errors.enValue && (
                    <p className="text-destructive text-sm mt-1">{errors.enValue.message}</p>
                  )}
                </div>

                <Button type="submit" disabled={isSubmitting} className="w-full">
                  {isSubmitting ? (
                    "Adding..."
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Add Translation
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* View Existing Translations */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Existing Translations</CardTitle>
              <CardDescription>Browse and manage existing translation keys</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
                <TabsList className="w-full justify-start overflow-x-auto">
                  {categories.map((cat) => (
                    <TabsTrigger key={cat} value={cat}>
                      {cat}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {categories.map((category) => (
                  <TabsContent key={category} value={category} className="space-y-4 mt-4">
                    <div className="space-y-3">
                      {Object.keys(frTranslations[category] || {}).map((key) => (
                        <Card key={key} className="p-4 border-border">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-grow space-y-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{key}</Badge>
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm">
                                  <span className="font-semibold text-foreground">FR:</span>{" "}
                                  <span className="text-muted-foreground">
                                    {frTranslations[category]?.[key] || "-"}
                                  </span>
                                </p>
                                <p className="text-sm">
                                  <span className="font-semibold text-foreground">EN:</span>{" "}
                                  <span className="text-muted-foreground">
                                    {enTranslations[category]?.[key] || "-"}
                                  </span>
                                </p>
                              </div>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-destructive">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Translation Key?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete the translation key "{category}.{key}"
                                    from both French and English translations. This action cannot be
                                    undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteKey(category, key)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </Card>
                      ))}
                      {Object.keys(frTranslations[category] || {}).length === 0 && (
                        <p className="text-muted-foreground text-sm text-center py-8">
                          No translations in this category yet
                        </p>
                      )}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Statistics */}
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg">Total Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-primary">{categories.length}</p>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg">French Keys</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-accent">
                {Object.values(frTranslations).reduce(
                  (acc, cat) => acc + Object.keys(cat).length,
                  0
                )}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg">English Keys</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-accent">
                {Object.values(enTranslations).reduce(
                  (acc, cat) => acc + Object.keys(cat).length,
                  0
                )}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default I18nManager;
